import { useState, useCallback } from 'react';
import {
  fetchFivSession, fetchFivDonors, saveFivSession,
  saveFivSessionLegacy,
} from '../data/fiv';
import { fetchSemenDaSessao } from '../data/semen';
import { fetchEmbryosByService } from '../data/embryos';
import { getPending } from '../data/local/pending';
import {
  FIV_STAGE_FIELDS, FIV_DT_STAGE_FIELDS,
  FIV_VITRI_STAGE_FIELDS, FIV_DESC_STAGE_FIELDS,
} from '../constants/workflowGranular';

/**
 * Hook para persistência da sessão de FIV nas tabelas normalizadas.
 * Embriões são auto-gerados a partir das contagens de estágio + destinos agregados.
 */
export const useFIVSession = (laboratoryId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Deriva contagens de estágio e destino a partir de registros individuais de embriões.
   * Usado para backward compat ao carregar serviços antigos.
   */
  const deriveCountsFromEmbryos = (embryos) => {
    const counts = { stage_bi: 0, stage_bl: 0, stage_bx: 0, stage_bn: 0, stage_be: 0 };
    const dests  = {
      dest_fresh: 0, dest_dt: 0, dest_vitri: 0, dest_dt_obs: '', dest_vitri_obs: '',
      dest_dt_bi: 0, dest_dt_bl: 0, dest_dt_bx: 0, dest_dt_bn: 0, dest_dt_be: 0,
      dest_vitri_bi: 0, dest_vitri_bl: 0, dest_vitri_bx: 0, dest_vitri_bn: 0,
      dest_vitri_be: 0,
      dest_desc: 0,
      dest_desc_bi: 0, dest_desc_bl: 0, dest_desc_bx: 0, dest_desc_bn: 0,
      dest_desc_be: 0,
    };

    // Sufixo do estágio, uma vez só: cada grupo usa o mesmo mapa.
    const sufixo = (stage) =>
      stage === 'BI' ? 'bi'
      : stage === 'BL' ? 'bl'
      : stage === 'BX' ? 'bx'
      : stage === 'BN' ? 'bn'
      : (stage === 'BX-ECLO' || stage === 'Be' || stage === 'BE') ? 'be'
      : null;

    for (const e of embryos) {
      const sf = sufixo(e.stage);

      // Os estágios de cima contam SÓ quem vai (ou foi) para a TE. Contar o
      // congelado aqui de novo o faria aparecer duas vezes, já que agora as
      // duas tabelas somam.
      if (e.destination === 'fresh' || e.destination === 'transferred') {
        if (sf) counts[`stage_${sf}`]++;
        dests.dest_fresh++;
      } else if (e.destination === 'frozen') {
        if (e.freeze_method === 'Direct Transfer') {
          dests.dest_dt++;
          if (e.freeze_location && !dests.dest_dt_obs) dests.dest_dt_obs = e.freeze_location;
          if (sf) dests[`dest_dt_${sf}`]++;
        } else {
          dests.dest_vitri++;
          if (e.freeze_location && !dests.dest_vitri_obs) dests.dest_vitri_obs = e.freeze_location;
          if (sf) dests[`dest_vitri_${sf}`]++;
        }
      } else if (e.destination === 'discard') {
        dests.dest_desc++;
        if (sf) dests[`dest_desc_${sf}`]++;
      }
    }

    return { ...counts, ...dests };
  };

  /**
   * Carrega sessão FIV de um serviço.
   */
  const loadFIVData = useCallback(async (serviceId) => {
    if (!serviceId || !laboratoryId) return null;
    setLoading(true);
    setError(null);
    try {
      const pendente = await getPending('fiv', serviceId);
      if (pendente) return pendente;

      const session = await fetchFivSession(serviceId);
      if (!session) return null;

      const donors = await fetchFivDonors(session.id);

      // Palhetas descongeladas nesta sessão. Deixado estourar de propósito: se
      // esta leitura falhasse em silêncio, a tela abriria sem sêmen nenhum e a
      // primeira gravação apagaria as palhetas lançadas de verdade.
      const semenUso = await fetchSemenDaSessao(session.id);

      // Para backward compat: se donor não tem stage counts, carrega embryos para derivar
      const donorIds = (donors || []).map(d => d.id);
      let embryosByDonor = {};

      if (donorIds.length > 0) {
        const embryos = await fetchEmbryosByService(serviceId);

        const donorByAnimalId = {};
        (donors || []).forEach(d => { if (d.animal_id) donorByAnimalId[d.animal_id] = d.id; });

        (embryos || []).forEach(e => {
          const targetDonorId = donorIds.includes(e.fiv_donor_id)
            ? e.fiv_donor_id
            : (e.donor_id ? donorByAnimalId[e.donor_id] : null);
          if (!targetDonorId) return;
          if (!embryosByDonor[targetDonorId]) embryosByDonor[targetDonorId] = [];
          embryosByDonor[targetDonorId].push(e);
        });
      }

      return {
        _session_id: session.id,
        session: {
          reception_date: session.reception_date,
          reception_time: session.reception_time,
          embryologist_name: session.embryologist_name,
          opu_link: session.opu_session_id,
          semen: (semenUso || []).map(u => ({
            semen_batch_id: u.semen_batch_id,
            doses: Number(u.doses),
            observacoes: u.observacoes || '',
          })),
        },
        donors_processing: (donors || []).map(d => {
          // hasNewFields: as colunas novas já têm dados reais (não apenas o DEFAULT 0)
          const hasNewFields = (d.stage_bi > 0 || d.stage_bl > 0 || d.stage_bx > 0 || d.stage_bn > 0 || d.stage_be > 0)
            || (d.dest_fresh > 0 || d.dest_dt > 0 || d.dest_vitri > 0 || d.dest_desc > 0);

          // Se não tem dados novos, tenta derivar dos embriões individuais (backward compat)
          const derived = !hasNewFields && embryosByDonor[d.id]?.length > 0
            ? deriveCountsFromEmbryos(embryosByDonor[d.id])
            : null;

          const rawEmbryos = embryosByDonor[d.id] || [];

          return {
            _db_id: d.id,
            id: d.id,
            donor_id: d.animal_id,
            donor_identifier: d.donor_identifier,
            bull: d.bull_name,
            oocytes_received: d.oocytes_received,
            viable_oocytes: d.viable_oocytes,
            cleaved_oocytes: d.cleaved_oocytes || 0,
            from_opu: d.from_opu,
            semen_batch_id: d.semen_batch_id || null,
            gotas: d.gotas || null,
            // Stage counts: usa valores do DB se já foram salvos, senão deriva dos embriões antigos
            stage_bi: hasNewFields ? (d.stage_bi || 0) : (derived?.stage_bi || 0),
            stage_bl: hasNewFields ? (d.stage_bl || 0) : (derived?.stage_bl || 0),
            stage_bx: hasNewFields ? (d.stage_bx || 0) : (derived?.stage_bx || 0),
            stage_bn: hasNewFields ? (d.stage_bn || 0) : (derived?.stage_bn || 0),
            stage_be: hasNewFields ? (d.stage_be || 0) : (derived?.stage_be || 0),
            // Destinos
            dest_fresh:     hasNewFields ? (d.dest_fresh  || 0) : (derived?.dest_fresh  || 0),
            dest_dt:        hasNewFields ? (d.dest_dt     || 0) : (derived?.dest_dt     || 0),
            dest_vitri:     hasNewFields ? (d.dest_vitri  || 0) : (derived?.dest_vitri  || 0),
            dest_dt_obs:    hasNewFields ? (d.dest_dt_obs    || '') : (derived?.dest_dt_obs    || ''),
            dest_vitri_obs: hasNewFields ? (d.dest_vitri_obs || '') : (derived?.dest_vitri_obs || ''),
            dest_dt_bi: hasNewFields ? (d.dest_dt_bi || 0) : (derived?.dest_dt_bi || 0),
            dest_dt_bl: hasNewFields ? (d.dest_dt_bl || 0) : (derived?.dest_dt_bl || 0),
            dest_dt_bx: hasNewFields ? (d.dest_dt_bx || 0) : (derived?.dest_dt_bx || 0),
            dest_dt_bn: hasNewFields ? (d.dest_dt_bn || 0) : (derived?.dest_dt_bn || 0),
            dest_dt_be: hasNewFields ? (d.dest_dt_be || 0) : (derived?.dest_dt_be || 0),
            dest_vitri_bi: hasNewFields ? (d.dest_vitri_bi || 0) : (derived?.dest_vitri_bi || 0),
            dest_vitri_bl: hasNewFields ? (d.dest_vitri_bl || 0) : (derived?.dest_vitri_bl || 0),
            dest_vitri_bx: hasNewFields ? (d.dest_vitri_bx || 0) : (derived?.dest_vitri_bx || 0),
            dest_vitri_bn: hasNewFields ? (d.dest_vitri_bn || 0) : (derived?.dest_vitri_bn || 0),
            dest_vitri_be: hasNewFields ? (d.dest_vitri_be || 0) : (derived?.dest_vitri_be || 0),
            dest_desc:    hasNewFields ? (d.dest_desc    || 0) : (derived?.dest_desc    || 0),
            dest_desc_bi: hasNewFields ? (d.dest_desc_bi || 0) : (derived?.dest_desc_bi || 0),
            dest_desc_bl: hasNewFields ? (d.dest_desc_bl || 0) : (derived?.dest_desc_bl || 0),
            dest_desc_bx: hasNewFields ? (d.dest_desc_bx || 0) : (derived?.dest_desc_bx || 0),
            dest_desc_bn: hasNewFields ? (d.dest_desc_bn || 0) : (derived?.dest_desc_bn || 0),
            dest_desc_be: hasNewFields ? (d.dest_desc_be || 0) : (derived?.dest_desc_be || 0),
            dest_desc_obs: hasNewFields ? (d.dest_desc_obs || '') : '',
            // Os embriões como o banco os guarda, um a um. A tela conta por
            // estágio; isto aqui é a leitura crua, usada por quem precisa da
            // numeração e do destino de cada palheta.
            embryos: rawEmbryos.map(e => ({
              id: e.id,
              embryo_number: e.service_number,
              quality: e.quality,
              stage: e.stage,
              destination: (e.destination === 'discard' && !e.recipient_id) ? 'fresh' : e.destination,
              freeze_data: e.freeze_date || e.freeze_method || e.freeze_location
                ? { date: e.freeze_date, method: e.freeze_method, location: e.freeze_location }
                : null
            })),
          };
        }),
        completed: session.completed,
        completed_at: session.completed_at
      };
    } catch (err) {
      console.error('useFIVSession.loadFIVData:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laboratoryId]);

  /**
   * Salva sessão FIV.
   *
   * Com produção lançada (contagem por estágio ou algum destino), delega tudo
   * para a função SQL `save_fiv_session`, que reconcilia numa transação só.
   *
   * Sem nenhuma produção — FIV aberta, doadoras importadas da OPU, nada
   * produzido ainda — não há o que reconciliar, e cai no caminho de gravação
   * linha a linha logo abaixo.
   */
  const saveFIVData = useCallback(async (serviceId, fivData) => {
    if (!serviceId || !laboratoryId || !fivData) return null;
    setLoading(true);
    setError(null);
    try {
      const { session, donors_processing = [], completed, completed_at } = fivData;

      // Qualquer um dos QUATRO grupos basta. Enquanto isto olhava só para os
      // estágios e para o DT, a doadora que apenas congelou caía no caminho
      // legado — foi assim que o PCAG50 entrou com 16 vitrificados e nenhum
      // embrião.
      const hasNewFormat = donors_processing.some(d =>
        FIV_STAGE_FIELDS.some(f => (parseInt(d[f.key]) || 0) > 0) ||
        FIV_DT_STAGE_FIELDS.some(f => (parseInt(d[f.key]) || 0) > 0) ||
        FIV_VITRI_STAGE_FIELDS.some(f => (parseInt(d[f.key]) || 0) > 0) ||
        FIV_DESC_STAGE_FIELDS.some(f => (parseInt(d[f.key]) || 0) > 0) ||
        (parseInt(d.dest_fresh) || 0) > 0 ||
        (parseInt(d.dest_vitri) || 0) > 0 ||
        // `dest_dt` escalar faltava nesta lista, e é o mesmo buraco pelo qual
        // o PCAG50 passou: quem trouxesse só o total de DT, sem a quebra por
        // estágio, caía no caminho legado.
        (parseInt(d.dest_dt)    || 0) > 0 ||
        (parseInt(d.dest_desc)  || 0) > 0
      );

      // FIV SUBSTITUÍDA PELA OPU vai pela RPC mesmo zerada. O caminho legado
      // não grava as contagens (ver abaixo): a doadora que continua guardaria
      // os clivados e embriões de antes, e "descartar" não descartaria nada;
      // e as doadoras que saem deixariam os embriões delas soltos no serviço.
      // A RPC grava os zeros e reconcilia os embriões. Exceção: a tela
      // estreita lançou embrião a embrião, formato que só o legado entende.
      const substituida = fivData._substituida
        && !donors_processing.some(d => d.embryos?.length > 0);

      // ── Novo formato: uma única chamada RPC ─────────────────────────────
      if (hasNewFormat || substituida) {
        return await saveFivSession({
          p_service_id:    serviceId,
          p_laboratory_id: laboratoryId,
          p_session: {
            reception_date:    session?.reception_date    || null,
            reception_time:    session?.reception_time    || null,
            embryologist_name: session?.embryologist_name || null,
            opu_link:          session?.opu_link          || null,
            completed:         completed || false,
            completed_at:      completed ? (completed_at || new Date().toISOString()) : null,
            // A chave só vai quando o sêmen foi de fato carregado. Ausente, o
            // banco preserva o que está gravado; `[]` significa "tirei todas as
            // palhetas". Mandar `[]` por engano apagaria o lançamento.
            ...(Array.isArray(session?.semen) ? {
              semen: session.semen
                .filter(s => s?.semen_batch_id)
                .map(s => ({
                  semen_batch_id: s.semen_batch_id,
                  doses:          Number(s.doses) || 1,
                  observacoes:    s.observacoes || null,
                })),
            } : {}),
          },
          p_donors: donors_processing.map(d => ({
            id:               d.id,
            donor_id:         d.donor_id         || null,
            donor_identifier: d.donor_identifier || null,
            bull:             d.bull || d.bull_name || null,
            oocytes_received: parseInt(d.oocytes_received) || 0,
            viable_oocytes:   parseInt(d.viable_oocytes)   || 0,
            cleaved_oocytes:  parseInt(d.cleaved_oocytes)  || 0,
            from_opu:         d.from_opu || false,
            semen_batch_id:   d.semen_batch_id || null,
            gotas:            parseInt(d.gotas) || null,
            stage_bi: parseInt(d.stage_bi) || 0,
            stage_bl: parseInt(d.stage_bl) || 0,
            stage_bx: parseInt(d.stage_bx) || 0,
            stage_bn: parseInt(d.stage_bn) || 0,
            stage_be: parseInt(d.stage_be) || 0,
            dest_fresh:     parseInt(d.dest_fresh)  || 0,
            dest_dt:        parseInt(d.dest_dt)     || 0,
            dest_vitri:     parseInt(d.dest_vitri)  || 0,
            dest_dt_obs:    d.dest_dt_obs    || null,
            dest_vitri_obs: d.dest_vitri_obs || null,
            dest_dt_bi: parseInt(d.dest_dt_bi) || 0,
            dest_dt_bl: parseInt(d.dest_dt_bl) || 0,
            dest_dt_bx: parseInt(d.dest_dt_bx) || 0,
            dest_dt_bn: parseInt(d.dest_dt_bn) || 0,
            dest_dt_be: parseInt(d.dest_dt_be) || 0,
            dest_vitri_bi: parseInt(d.dest_vitri_bi) || 0,
            dest_vitri_bl: parseInt(d.dest_vitri_bl) || 0,
            dest_vitri_bx: parseInt(d.dest_vitri_bx) || 0,
            dest_vitri_bn: parseInt(d.dest_vitri_bn) || 0,
            dest_vitri_be: parseInt(d.dest_vitri_be) || 0,
            // O descarte ia para o banco mas nunca voltava: `dest_desc*` não
            // estava na leitura acima. Quem descartava via o campo zerar ao
            // reabrir a tela, e o `save` seguinte apagava os embriões.
            dest_desc:     parseInt(d.dest_desc)     || 0,
            dest_desc_bi:  parseInt(d.dest_desc_bi)  || 0,
            dest_desc_bl:  parseInt(d.dest_desc_bl)  || 0,
            dest_desc_bx:  parseInt(d.dest_desc_bx)  || 0,
            dest_desc_bn:  parseInt(d.dest_desc_bn)  || 0,
            dest_desc_be:  parseInt(d.dest_desc_be)  || 0,
            dest_desc_obs: d.dest_desc_obs || null,
          })),
        }, fivData);
      }

      // ── Sem produção lançada: grava sessão e doadoras, linha a linha ────
      // Tudo é montado aqui e gravado por UMA operação. Antes eram cinco
      // escritas encadeadas pelo id da sessão — o que não existe offline.
      const sessionPayload = {
        service_id:        serviceId,
        laboratory_id:     laboratoryId,
        reception_date:    session?.reception_date    || null,
        reception_time:    session?.reception_time    || null,
        embryologist_name: session?.embryologist_name || null,
        opu_session_id:    session?.opu_link          || null,
        completed:         completed || false,
        completed_at:      completed ? (completed_at || new Date().toISOString()) : null,
      };

      // Sem `fiv_session_id`: quem preenche é a operação.
      const donorsPayload = donors_processing.map((d, idx) => {
        const isNew    = !d.id || String(d.id).startsWith('temp-');
        const stableId = isNew ? crypto.randomUUID() : d.id;
        return {
          id: stableId, laboratory_id: laboratoryId,
          sort_order: idx, animal_id: d.donor_id || null,
          donor_identifier: d.donor_identifier || null,
          bull_name: d.bull || d.bull_name || null,
          oocytes_received: parseInt(d.oocytes_received) || 0,
          viable_oocytes:   parseInt(d.viable_oocytes)   || 0,
          cleaved_oocytes:  parseInt(d.cleaved_oocytes)  || 0,
          from_opu: d.from_opu || false,
          // As contagens por estágio e os destinos NÃO entram aqui.
          //
          // Entravam, fixados em zero, e era a pior linha do workflow: este
          // caminho é o que a tela estreita usa, e ela só sabe preencher
          // embrião a embrião. Um único "Salvar" numa janela estreita zerava
          // a vitrificação, o DT e o descarte que a tela larga tinha lançado —
          // e, zerados, `temCamposNovos` passava a dar falso e a tela voltava
          // a derivar tudo de `embryos`, perdendo de vez a distinção entre
          // congelado e descartado.
          //
          // Fora do payload, a gravação não as menciona: linha nova nasce com
          // o padrão da coluna, linha existente conserva o que já tinha. Este
          // caminho não sabe nada sobre estágios, e o certo é não opinar.
        };
      });

      let globalNum = 1;
      const embrioesPorDoadora = donors_processing.map((d, idx) => {
        const fivDonorId = donorsPayload[idx].id;
        if (!(d.embryos?.length > 0)) return { fivDonorId, linhas: [] };
        const inicio = globalNum;
        globalNum += d.embryos.length;
        return {
          fivDonorId,
          linhas: d.embryos.map((e, ei) => ({
            laboratory_id: laboratoryId, service_id: serviceId,
            service_number: inicio + ei,
            fiv_donor_id: fivDonorId, donor_id: d.donor_id || null,
            bull_name: d.bull || d.bull_name || null,
            quality: e.quality || null, stage: e.stage || null,
            destination: e.destination || 'fresh',
            freeze_date:     e.freeze_data?.date     || null,
            freeze_method:   e.freeze_data?.method   || null,
            freeze_location: e.freeze_data?.location || null,
          })),
        };
      });

      return await saveFivSessionLegacy(
        sessionPayload, donorsPayload, embrioesPorDoadora, laboratoryId, fivData,
      );
    } catch (err) {
      console.error('useFIVSession.saveFIVData:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laboratoryId]);

  return { loading, error, loadFIVData, saveFIVData };
};
